/**
 * Favotrip Exclusive: op welke markt het ledenoppervlak bestaat.
 *
 * Dennis 25-10-2026: Exclusive komt ook voor Belgische leden op favotrip.be.
 * Fase 1 is favotrip.be in het NEDERLANDS (de rijke hotelteksten bestaan
 * alleen in het Nederlands); Frans is fase 2. Het inloggen op .be (BFF
 * `/api/klant/sessie`) is een aparte PR, daarom staat de vlag
 * `NEXT_PUBLIC_LUXE_BE` uit. Met de vlag uit is alles precies zoals vóór
 * deze regel: alleen favotrip.nl.
 *
 * Eén regel voor middleware, navigatie, poort, deelknop en belastingtekst:
 *   - favotrip.nl: altijd (zoals nu). Een onbekende host (localhost, een
 *     preview) valt net als in de middleware terug op favotrip.nl.
 *   - favotrip.be, markt nl-be met taal nl: alleen met de vlag aan.
 *   - favotrip.be in het Frans (fr-be), en elk ander domein: nee. Fr-be houdt
 *     de huidige poort (308 naar favotrip.nl), met op het poortscherm een
 *     Nederlandse knop "Bekijk in het Nederlands" (`?ft-lang=nl`).
 *   - staging: dezelfde regel voor het domein achter `staging.`; de
 *     middleware houdt een staging-host daarnaast altijd op zichzelf.
 *
 * Alleen publieke gegevens: host, marktsleutel en taal. Nooit iets uit de
 * sessie, de deal of een cookie.
 */
import { DOMAIN_CONFIGS, DEFAULT_DOMAIN, getMarketConfig, normalizeHostname } from "@/lib/site-routing";

export const LUXE_BE_DOMEIN = "favotrip.be";
export const LUXE_BE_MARKT = "nl-be";
export const LUXE_BE_ORIGIN = "https://www.favotrip.be";

/**
 * De vlag. Next.js plakt `process.env.NEXT_PUBLIC_LUXE_BE` bij de build in;
 * alleen precies "true" zet hem aan. Gelezen bij elke aanroep, zodat een test
 * hem per geval kan zetten.
 */
export function luxeBeAan() {
  return process.env.NEXT_PUBLIC_LUXE_BE === "true";
}

/**
 * Het Favotrip-domein van een host (zonder www./staging., zonder poort). Een
 * onbekende host wordt favotrip.nl, zoals in resolveSiteContext. Zonder host:
 * het domein van de marktsleutel.
 */
export function exclusiveDomein(host, marketKey) {
  const kaal = normalizeHostname(host ?? "").replace(/^www\./, "").replace(/^staging\./, "");
  if (!kaal) return marketKey ? getMarketConfig(marketKey).domain : DEFAULT_DOMAIN;
  return DOMAIN_CONFIGS.find((config) => config.domain === kaal)?.domain ?? DEFAULT_DOMAIN;
}

/** Nederlandstalig favotrip.be met de vlag aan: de enige nieuwe markt van fase 1. */
export function isLuxeBeMarkt(host, marketKey, locale) {
  return (
    luxeBeAan() &&
    exclusiveDomein(host, marketKey) === LUXE_BE_DOMEIN &&
    marketKey === LUXE_BE_MARKT &&
    locale === "nl"
  );
}

/**
 * Mag Exclusive op deze markt getoond worden (geen 308 naar favotrip.nl,
 * menulink aan, inloggen op het eigen domein)?
 *
 * @param {string | null | undefined} host  host of domein; leeg = domein van de markt
 * @param {string | null | undefined} marketKey  bv. "nl-nl", "nl-be", "fr-be"
 * @param {string | null | undefined} locale  bv. "nl", "fr"
 */
export function exclusiveToegestaan(host, marketKey, locale) {
  if (exclusiveDomein(host, marketKey) === DEFAULT_DOMAIN) return true;
  return isLuxeBeMarkt(host, marketKey, locale);
}

/**
 * Nederlandstalige Exclusive-markt voor teksten die alleen in het Nederlands
 * bestaan (de belastingwaarschuwing): nl-nl + nl, en met de vlag nl-be + nl.
 */
export function exclusiveNederlandseMarkt(marketKey, locale) {
  if (locale !== "nl") return false;
  return marketKey === "nl-nl" || (luxeBeAan() && marketKey === LUXE_BE_MARKT);
}

/**
 * fr-be met de vlag aan: de poort toont daar een knop naar de Nederlandse
 * versie (`?ft-lang=nl`); na die keuze is het nl-be en mag Exclusive.
 */
export function exclusiveNaarNederlands(host, marketKey, locale) {
  return (
    luxeBeAan() &&
    exclusiveDomein(host, marketKey) === LUXE_BE_DOMEIN &&
    !exclusiveToegestaan(host, marketKey, locale)
  );
}
